import React, { useState, useEffect, useCallback } from 'react';
import OlcButton from '~/components/General/OlcButton';
import VerificationInput from 'react-verification-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  setup2FA,
  verifySetup2FA,
  resend2FACode,
} from '~/redux/actions/two-factor-actions';
import { failure } from '~/redux/actions/snackbar-actions';
import { CLEAR_2FA_STATE } from '~/redux/actions/action-types';
import '../VerificationModal/styles.scss';
import './styles.scss';

const RESEND_COOLDOWN = 60;
const STEP_INTRO = 'intro';
const STEP_VERIFY = 'verify';
const STEP_SUCCESS = 'success';

const TwoFactorSetupFlow = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requires2FASetup, tempToken, maskedEmail } = useSelector(
    (state) => state.twoFactorReducer
  );

  const [step, setStep] = useState(STEP_INTRO);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!requires2FASetup) {
      setStep(STEP_INTRO);
      setCode('');
      setError('');
    }
  }, [requires2FASetup]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    const result = await dispatch(setup2FA(tempToken));
    setLoading(false);
    if (result?.message === 'success') {
      setStep(STEP_VERIFY);
      setCooldown(RESEND_COOLDOWN);
    } else {
      const msg = result?.response?.data?.message || 'Failed to send code';
      dispatch(failure(msg));
    }
  };

  const handleVerify = useCallback(
    async (verifyCode = code) => {
      if (verifyCode.length !== 6 || loading) return;
      setError('');
      setLoading(true);
      const result = await dispatch(verifySetup2FA(verifyCode, tempToken));
      setLoading(false);
      if (result?.message === 'success') {
        setStep(STEP_SUCCESS);
        setTimeout(() => {
          dispatch({ type: CLEAR_2FA_STATE });
          navigate('/');
        }, 1500);
      } else {
        const msg = result?.response?.data?.message || 'Verification failed';
        setError(msg);
        setCode('');
      }
    },
    [code, tempToken, loading, dispatch, navigate]
  );

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    const result = await dispatch(resend2FACode(tempToken));
    setResending(false);
    if (result?.message === 'success') {
      setCooldown(RESEND_COOLDOWN);
    } else {
      const msg = result?.response?.data?.message || 'Failed to resend code';
      dispatch(failure(msg));
    }
  };

  const handleCancel = () => {
    dispatch({ type: CLEAR_2FA_STATE });
    setCode('');
    setError('');
    setStep(STEP_INTRO);
  };

  if (!requires2FASetup) return null;

  return (
    <Dialog
      open={requires2FASetup}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      className="twoFactorModal"
    >
      <DialogContent className="twoFactorContent">
        {step === STEP_INTRO && (
          <>
            <Box className="setupIconWrapper">
              <LockOutlinedIcon className="setupIcon" />
            </Box>
            <Box className="twoFactorHeader">
              <Typography variant="h5">Set Up Two-Factor Authentication</Typography>
              <Typography variant="body2" className="twoFactorSubtext">
                Your account requires two-factor authentication for added security.
                We&apos;ll send a verification code to <strong>{maskedEmail}</strong>.
              </Typography>
            </Box>
            <OlcButton
              variant="primary"
              className="twoFactorSubmitBtn"
              disabled={loading}
              onClick={handleSendCode}
            >
              {loading ? (
                <CircularProgress sx={{ color: 'white', width: '22px !important', height: '22px !important' }} />
              ) : (
                'Send Verification Code'
              )}
            </OlcButton>
            <OlcButton variant="secondary" className="twoFactorCancelBtn" onClick={handleCancel}>
              Back to Login
            </OlcButton>
          </>
        )}

        {step === STEP_VERIFY && (
          <>
            <Box className="twoFactorHeader">
              <Typography variant="h5">Enter Verification Code</Typography>
              <Typography variant="body2" className="twoFactorSubtext">
                We sent a 6-digit code to <strong>{maskedEmail}</strong>
              </Typography>
            </Box>

            <Box className="twoFactorInputWrapper">
              <VerificationInput
                classNames={{
                  container: 'twoFactorInputContainer',
                  character: 'twoFactorCharacter',
                  characterInactive: 'twoFactorCharacter--inactive',
                  characterSelected: 'twoFactorCharacter--selected',
                }}
                placeholder=""
                length={6}
                value={code}
                autoFocus
                onChange={(val) => {
                  setCode(val);
                  setError('');
                }}
                onComplete={(val) => handleVerify(val)}
              />
            </Box>

            {error && (
              <Typography className="twoFactorError">{error}</Typography>
            )}

            <OlcButton
              variant="primary"
              className="twoFactorSubmitBtn"
              disabled={code.length !== 6 || loading}
              onClick={() => handleVerify()}
            >
              {loading ? (
                <CircularProgress sx={{ color: 'white', width: '22px !important', height: '22px !important' }} />
              ) : (
                'Enable Two-Factor Authentication'
              )}
            </OlcButton>

            <Box className="twoFactorActions">
              <OlcButton
                variant="secondary"
                className="twoFactorResendBtn"
                disabled={cooldown > 0 || resending}
                onClick={handleResend}
              >
                {resending
                  ? 'Sending...'
                  : cooldown > 0
                    ? `Resend Code (${cooldown}s)`
                    : 'Resend Code'}
              </OlcButton>
              <OlcButton variant="secondary" className="twoFactorCancelBtn" onClick={handleCancel}>
                Back to Login
              </OlcButton>
            </Box>
          </>
        )}

        {step === STEP_SUCCESS && (
          <Box className="setupSuccess">
            <CheckCircleOutlineIcon className="setupSuccessIcon" />
            <Typography variant="h5">Two-Factor Authentication Enabled</Typography>
            <Typography variant="body2" className="twoFactorSubtext">
              Your account is now protected. Redirecting...
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorSetupFlow;
